import React, { useState, useEffect, useContext } from 'react';
import { UserContext } from '../../contexts/UserContext';
import { X, Plus, FileText, TrendingUp, TrendingDown, Upload, Download, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = `${process.env.REACT_APP_API_URL?.replace(/\/$/, '') || ''}/api`;

const AmendmentHistory = ({ contract, onClose, onBack }) => {
  const { user } = useContext(UserContext);
  const [amendments, setAmendments] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    amendment_number: '',
    effective_date: '',
    previous_rate: contract.current_rate,
    new_rate: '',
    reason: '',
    pdf_document: null
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchAmendments();
  }, []);

  const fetchAmendments = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/contracts/${contract.id}/amendments/`,
        { headers: { Authorization: `Bearer ${user.accessToken}` } }
      );
      setAmendments(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching amendments:', error);
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === 'file') {
      setFormData(prev => ({ ...prev, [name]: files[0] }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.amendment_number) newErrors.amendment_number = 'Amendment number is required';
    if (!formData.effective_date) newErrors.effective_date = 'Effective date is required';
    if (!formData.new_rate || formData.new_rate <= 0) {
      newErrors.new_rate = 'Valid new rate is required';
    }
    if (!formData.reason) newErrors.reason = 'Reason is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      const submitData = new FormData();

      Object.keys(formData).forEach(key => {
        if (key === 'pdf_document') {
          if (formData[key] instanceof File) {
            submitData.append(key, formData[key]);
          }
        } else if (formData[key] !== null && formData[key] !== '') {
          submitData.append(key, formData[key]);
        }
      });

      await axios.post(
        `${API_BASE_URL}/contracts/${contract.id}/amendments/`,
        submitData,
        {
          headers: {
            Authorization: `Bearer ${user.accessToken}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      // Reset form
      setFormData({
        amendment_number: '',
        effective_date: '',
        previous_rate: formData.new_rate,
        new_rate: '',
        reason: '',
        pdf_document: null
      });
      setShowForm(false);
      fetchAmendments();
    } catch (error) {
      console.error('Error creating amendment:', error);
      if (error.response?.data) {
        setErrors(error.response.data);
      } else {
        alert('Error creating amendment. Please try again.');
      }
    }
  };

  const getRateChange = (amendment) => {
    const change = amendment.new_rate - amendment.previous_rate;
    const percentChange = ((change / amendment.previous_rate) * 100).toFixed(2);

    if (change > 0) {
      return {
        icon: <TrendingUp className="w-4 h-4 text-green-600" />,
        text: `+${change.toFixed(2)} (+${percentChange}%)`,
        color: 'text-green-600'
      };
    } else if (change < 0) {
      return {
        icon: <TrendingDown className="w-4 h-4 text-red-600" />,
        text: `${change.toFixed(2)} (${percentChange}%)`,
        color: 'text-red-600'
      };
    } else {
      return {
        icon: null,
        text: 'No change',
        color: 'text-gray-600'
      };
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="bg-white rounded-lg shadow">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Amendment History</h2>
            <p className="text-sm text-gray-600">
              Contract: {contract.contract_number} - {contract.user_name}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {!showForm && (
              <button
                onClick={() => setShowForm(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                <Plus className="w-4 h-4" />
                New Amendment
              </button>
            )}
            <button
              onClick={onClose || onBack}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* New Amendment Form */}
        {showForm && (
          <form onSubmit={handleSubmit} className="p-6 border-b bg-blue-50 space-y-4">
            <h3 className="font-medium text-gray-900 mb-4">Create New Amendment</h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amendment Number *
                </label>
                <input
                  type="text"
                  name="amendment_number"
                  value={formData.amendment_number}
                  onChange={handleChange}
                  placeholder="e.g., Amendment 1"
                  className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.amendment_number ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.amendment_number && (
                  <p className="mt-1 text-sm text-red-600">{errors.amendment_number}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Effective Date *
                </label>
                <input
                  type="date"
                  name="effective_date"
                  value={formData.effective_date}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.effective_date ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.effective_date && (
                  <p className="mt-1 text-sm text-red-600">{errors.effective_date}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Previous Rate
                </label>
                <input
                  type="number"
                  name="previous_rate"
                  value={formData.previous_rate}
                  onChange={handleChange}
                  step="0.01"
                  readOnly
                  className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-100"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Rate * ({contract.currency})
                </label>
                <input
                  type="number"
                  name="new_rate"
                  value={formData.new_rate}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.new_rate ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.new_rate && (
                  <p className="mt-1 text-sm text-red-600">{errors.new_rate}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason for Amendment *
              </label>
              <textarea
                name="reason"
                value={formData.reason}
                onChange={handleChange}
                rows={3}
                placeholder="Explain the reason for this rate change..."
                className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.reason ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.reason && (
                <p className="mt-1 text-sm text-red-600">{errors.reason}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Amendment PDF
              </label>
              <label className="flex items-center justify-center px-4 py-2 border-2 border-dashed border-gray-300 rounded-md cursor-pointer hover:border-blue-500">
                <Upload className="w-4 h-4 mr-2 text-gray-400" />
                <span className="text-sm text-gray-600">
                  {formData.pdf_document?.name || 'Choose file or drag here'}
                </span>
                <input
                  type="file"
                  name="pdf_document"
                  onChange={handleChange}
                  accept=".pdf"
                  className="hidden"
                />
              </label>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setErrors({});
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Create Amendment
              </button>
            </div>
          </form>
        )}

        {/* Amendments List */}
        <div className="p-6">
          {loading ? (
            <div className="text-center py-8">Loading amendments...</div>
          ) : amendments.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No amendments yet</p>
              <p className="text-sm text-gray-400 mt-2">
                Create an amendment to track rate changes over time
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="text-sm font-medium text-gray-700 mb-2">
                Timeline ({amendments.length} amendment{amendments.length !== 1 ? 's' : ''})
              </div>

              {amendments.map((amendment, index) => {
                const rateChange = getRateChange(amendment);
                const isLatest = index === 0;

                return (
                  <div
                    key={amendment.id}
                    className={`border rounded-lg p-4 ${
                      isLatest ? 'border-blue-300 bg-blue-50' : 'border-gray-200'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-gray-900">
                            {amendment.amendment_number}
                          </h4>
                          {isLatest && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-blue-600 text-white rounded">
                              Current
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">
                          Effective: {new Date(amendment.effective_date).toLocaleDateString()}
                        </p>
                      </div>
                      {amendment.pdf_document && (
                        <a
                          href={amendment.pdf_document}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <Download className="w-4 h-4" />
                        </a>
                      )}
                    </div>

                    <div className="grid grid-cols-3 gap-4 mb-3">
                      <div>
                        <p className="text-xs text-gray-500">Previous Rate</p>
                        <p className="font-medium text-gray-900">
                          {amendment.previous_rate} {contract.currency}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">New Rate</p>
                        <p className="font-medium text-gray-900">
                          {amendment.new_rate} {contract.currency}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Change</p>
                        <div className={`flex items-center gap-1 font-medium ${rateChange.color}`}>
                          {rateChange.icon}
                          <span>{rateChange.text}</span>
                        </div>
                      </div>
                    </div>

                    <div className="border-t pt-3">
                      <p className="text-xs text-gray-500 mb-1">Reason</p>
                      <p className="text-sm text-gray-700">{amendment.reason}</p>
                    </div>

                    <div className="mt-2 text-xs text-gray-400">
                      Created {new Date(amendment.created_at).toLocaleDateString()} by {amendment.created_by_name}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AmendmentHistory;
